import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Trash2, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import api from "@/api/client";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const nav = useNavigate();
  const [stockMap, setStockMap] = useState({});

  // Fetch latest stock per cart item so the + button can cap at available stock.
  useEffect(() => {
    let cancelled = false;
    const ids = Array.from(new Set((items || []).map((i) => i.product_id)));
    if (!ids.length) return;
    (async () => {
      const entries = await Promise.all(ids.map(async (id) => {
        try { const r = await api.get(`/products/${id}`); return [id, Number(r.data?.stock || 0)]; }
        catch { return [id, Infinity]; }
      }));
      if (cancelled) return;
      setStockMap(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [items.map((i) => i.product_id).join(",")]);

  return (
    <div className="mx-auto max-w-6xl px-4 lg:px-8 py-10" data-testid="cart-page">
      <h1 className="font-display text-4xl mb-8">Your Cart</h1>
      {items.length === 0 ? (
        <div className="text-center py-20">
          <ShoppingBag className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 mb-6">Your cart is empty.</p>
          <Link to="/"><Button data-testid="cart-shop-btn" className="rounded-full bg-[#F48E2E] hover:bg-[#D97815]">Shop today's drop</Button></Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-3">
            {items.map((it) => {
              const stock = stockMap[it.product_id];
              const atLimit = stock != null && it.quantity >= stock;
              return (
                <div key={`${it.product_id}-${it.size || ""}`} data-testid={`cart-item-${it.product_id}`} className="flex items-center gap-4 p-4 bg-white rounded-2xl border border-gray-200">
                  {it.image_base64 ? <img src={it.image_base64} alt={it.name} className="h-20 w-20 object-cover rounded-xl" /> : <div className="h-20 w-20 rounded-xl bg-orange-50" />}
                  <div className="flex-1">
                    <div className="font-medium">{it.name}</div>
                    <div className="text-sm text-gray-500">₹{it.price.toLocaleString("en-IN")}</div>
                    <div className="mt-2 flex items-center gap-3">
                      <div className="flex items-center border border-gray-200 rounded-full">
                        <button data-testid={`qty-dec-${it.product_id}`} onClick={() => updateQty(it.product_id, it.quantity - 1, it.size)} className="w-8 h-8">-</button>
                        <div className="w-6 text-center text-sm" data-testid={`qty-${it.product_id}`}>{it.quantity}</div>
                        <button
                          data-testid={`qty-inc-${it.product_id}`}
                          onClick={() => {
                            if (atLimit) { toast.error(`Only ${stock} in stock`); return; }
                            updateQty(it.product_id, it.quantity + 1, it.size);
                          }}
                          disabled={atLimit}
                          className="w-8 h-8 disabled:opacity-40 disabled:cursor-not-allowed"
                        >+</button>
                      </div>
                      <button data-testid={`remove-${it.product_id}`} onClick={() => removeItem(it.product_id, it.size)} className="text-gray-400 hover:text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </button>
                      {atLimit && <span className="text-[11px] text-amber-600" data-testid={`stock-cap-${it.product_id}`}>Max stock reached</span>}
                    </div>
                  </div>
                  <div className="font-display text-lg">₹{(it.price * it.quantity).toLocaleString("en-IN")}</div>
                </div>
              );
            })}
          </div>
          <div className="p-6 bg-white rounded-2xl border border-gray-200 h-fit">
            <div className="font-display text-xl mb-4">Order Summary</div>
            <div className="flex justify-between text-sm mb-2"><span>Subtotal</span><span>₹{subtotal.toLocaleString("en-IN")}</span></div>
            <div className="flex justify-between text-sm mb-4 text-gray-500"><span>Shipping</span><span>Calculated at checkout</span></div>
            <div className="border-t pt-3 flex justify-between font-display text-lg"><span>Total</span><span>₹{subtotal.toLocaleString("en-IN")}</span></div>
            <Button data-testid="checkout-btn" onClick={() => nav("/checkout")} className="w-full mt-5 rounded-full bg-[#F48E2E] hover:bg-[#D97815] h-11">Proceed to Checkout</Button>
            <p className="text-xs text-gray-500 mt-3">Shipping charges are extra and will be calculated based on the delivery city at checkout.</p>
          </div>
        </div>
      )}
    </div>
  );
}
